import type { KnowledgeParticle } from '../../types/domain';
import type { PasPracticeLibraryItem } from '../../utils/pasPracticeLibrary';
import { refreshPasPracticeItemStatus } from '../../utils/pasPracticeLibrary';
import type { SubjectCode } from '../../utils/subjectConfig';
import { buildRevisionPrintParticlesFromDeck } from '../review/print/buildPrintParticle';
import type { RevisionPrintParticle } from '../review/print/RevisionPrintAction';

function cleanPracticeQuestion(question: string): string {
  return String(question || '')
    .trim()
    .replace(/【变式\s*\d+】/g, '')
    .replace(/【台阶\s*\d+】/g, '')
    .replace(/^\[刻意练习\]\s*/i, '')
    .trim();
}

function isPasPracticeParticle(particle: KnowledgeParticle): boolean {
  const meta = particle.metadata && typeof particle.metadata === 'object' && !Array.isArray(particle.metadata)
    ? particle.metadata as Record<string, unknown>
    : null;
  if (meta?.pasPractice && typeof meta.pasPractice === 'object') return true;
  const tags = particle.tags;
  return Array.isArray(tags) && tags.some((tag) => String(tag) === 'pas_pkp' || String(tag) === '刻意练习');
}

function practiceItemPrintable(item: PasPracticeLibraryItem): boolean {
  const refreshed = refreshPasPracticeItemStatus(item);
  if (refreshed.negativeExample) return false;
  if (refreshed.status === 'expired') return false;
  return Boolean(String(refreshed.question || '').trim());
}

function buildPrintParticleFromPracticeItem(item: PasPracticeLibraryItem): RevisionPrintParticle {
  const questionMain = cleanPracticeQuestion(item.question);
  const answerMain = String(item.answer || '').trim();
  return {
    id: item.id,
    title: questionMain.slice(0, 48) || item.id,
    questionMain: questionMain || '未写入题面',
    spineLabel: null,
    sourceOrigin: 'pas_ladder',
    embeddedImageRecord: null,
    embeddedImage: null,
    answerMain,
    fullAnswer: answerMain,
    subQuestions: [],
    subQuestionsExpanded: false,
    subCards: [],
    familySummary: item.notes || null,
  };
}

/**
 * Prefer live practice-library drafts (what the workbench 题目库 shows).
 * Fall back to synced Practice KPs (QR scan-back) when the library is empty.
 */
export function buildLadderPracticePrintParticles(input: {
  particles: KnowledgeParticle[];
  libraryItems: PasPracticeLibraryItem[];
  subject?: SubjectCode;
}): RevisionPrintParticle[] {
  const seen = new Set<string>();
  const fromLibrary: RevisionPrintParticle[] = [];
  for (const item of input.libraryItems) {
    if (!practiceItemPrintable(item)) continue;
    const id = String(item.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    fromLibrary.push(buildPrintParticleFromPracticeItem(item));
  }
  if (fromLibrary.length > 0) return fromLibrary;

  const practiceKps = input.particles.filter(isPasPracticeParticle);
  if (practiceKps.length > 0) {
    return buildRevisionPrintParticlesFromDeck(
      practiceKps,
      'flashcard_drill',
      input.subject,
    ).map((particle) => ({
      ...particle,
      sourceOrigin: particle.sourceOrigin || 'pas_ladder',
    }));
  }

  return [];
}
